import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { useDisplayQuoteCurrencies } from '@flipster/features/src/shared/quote-currency/state';
import { TradFiCategory, getAvailableTradFiCategories } from '@flipster/features/src/shared/rwa';
import { useSymbols } from '@flipster/features/src/shared/symbol/use-symbols';
import { FormattedMessage } from '@flipster/i18n';
import { Carousel } from '@flipster/react/design-system-legacy/carousel';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { TextButton } from '@flipster/react/design-system/text-button';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { breakpointTB } from '@flipster/utils/constants/breakpoint';

import { PerpTopMarket } from '@web/app/[locale]/_perp-top-market';
import { getAllPerpCarouselOrder } from '@web/app/[locale]/_perp-top-market/carousel-order';
import { MarketCarouselType } from '@web/app/[locale]/_perp-top-market/constants';
import { SortedSymbolsCategory } from '@web/app/[locale]/_perp-top-market/types';
import { usePerpTopMarkets } from '@web/app/[locale]/_perp-top-market/use-perp-top-markets';

import { CategoryCarouselMode, marketPageBunja } from '../state';
import { TabValueType } from '../types';

const ModeTabs = createTabsComponent<CategoryCarouselMode>();

const carouselToTradFiCategory: Partial<Record<MarketCarouselType, TradFiCategory>> = {
  tradfiAll: 'all',
  tradfiPreIpo: 'preIpo',
  tradfiStocks: 'stock',
  tradfiEtf: 'etf',
  tradfiCommodity: 'commodity',
};
const tradFiCategoryToCarousel: Record<TradFiCategory, MarketCarouselType> = {
  all: 'tradfiAll',
  preIpo: 'tradfiPreIpo',
  stock: 'tradfiStocks',
  etf: 'tradfiEtf',
  commodity: 'tradfiCommodity',
};

interface Props {
  onChangeTab: (tab: TabValueType) => void;
}

export const CategoryCarousel = ({ onChangeTab }: Props) => {
  const { isMobile } = useBreakpoint();

  const { categoryCarouselModeAtom, setDiscoverTradfiCategory } = useBunja(marketPageBunja);
  const { symbols } = useSymbols('perpetual');
  const [carouselMode, setCarouselMode] = useAtom(categoryCarouselModeAtom);
  const { sortedTopMarkets } = usePerpTopMarkets();
  const displayQuoteCurrencies = useDisplayQuoteCurrencies();

  const handleClickSeeAll = (carouselId: MarketCarouselType) => (tab: SortedSymbolsCategory) => {
    onChangeTab(tab);
    const tradFiCategory = carouselToTradFiCategory[carouselId];
    if (tradFiCategory) setDiscoverTradfiCategory(tradFiCategory);
  };

  const layoutCardIds = getAllPerpCarouselOrder(symbols?.layout?.components);
  const allPerpIds = layoutCardIds.filter((id) => !!sortedTopMarkets[id]?.length);
  const tradfiIds = getAvailableTradFiCategories(symbols)
    .map((category) => tradFiCategoryToCarousel[category])
    .filter((id) => !!sortedTopMarkets[id]?.length);
  const hasTradfi = tradfiIds.length > 0;
  const carouselIds = hasTradfi && carouselMode === 'tradfi' ? tradfiIds : allPerpIds;

  const openPerpetualInfoAlert = useCallback(() => {
    dialog.open({
      title: {
        label: <FormattedMessage id='Market.r250922.Perpetual_swap' />,
      },
      content: (
        <div className={vstack({ gap: 16, alignItems: 'flex-start' })}>
          <p>
            <FormattedMessage id='Market.r250922.Perpetual_swaps_info' />
          </p>
          <TextButton
            color='passive'
            size='medium'
            as='a'
            href='/support/trading-rules'
            label={<FormattedMessage id='Market.r250922.Learn_perpetual_contracts' />}
            newTab
          />
        </div>
      ),
      buttons: {
        primary: <Dialog.Button hierarchy='primary' label={<FormattedMessage id='Common.r250401.Ok' />} />,
      },
    });
  }, []);

  const description = useMemo(() => {
    return (
      <div className={hstack({ textStyle: 'Body/XS/Monospace/Regular', color: 'content.default.level-2', gap: 2 })}>
        <p>
          <FormattedMessage
            id='Market.r251015.All_pairs_in_symbols'
            values={{ symbols: displayQuoteCurrencies.join(' / ') }}
          />
        </p>
        <button
          type='button'
          onClick={openPerpetualInfoAlert}
          className={css({ w: 16, h: 16, color: 'content.default.level-3' })}
        >
          <InformationCircleIcon size={16} />
        </button>
      </div>
    );
  }, [openPerpetualInfoAlert, displayQuoteCurrencies]);

  return (
    <Carousel
      slidesPerView='auto'
      spaceBetween={8}
      breakpoints={{ [breakpointTB]: { slidesPerView: 3, spaceBetween: 20 } }}
      title={
        <div className={hstack({ justify: 'flex-start', gap: 20 })}>
          <ModeTabs
            type='scrollable'
            size='large'
            border={false}
            value={carouselMode}
            onChange={setCarouselMode}
            css={{ w: 'auto' }}
          >
            <ModeTabs.Tab value='tradfi' label={<FormattedMessage id='Market.r260205.TradFi' />} />
            <ModeTabs.Tab value='allPerp' label={<FormattedMessage id='Market.r250923.All_perps' />} />
          </ModeTabs>
          {carouselMode === 'allPerp' && !isMobile && description}
        </div>
      }
      navigator={
        <Carousel.Navigator css={{ gridArea: 'arrow-right', pr: { base: 10, tablet: 0 }, alignItems: 'center' }} />
      }
      className={css({ pl: { base: 16, tablet: 0 }, gap: { base: 16, tablet: 24 } })}
    >
      {carouselIds.map((id) => (
        <Carousel.Slide css={{ w: { tabletDown: '310px' } }} key={`market-${id}`}>
          <PerpTopMarket carouselId={id} onClickSeeAll={handleClickSeeAll(id)} />
        </Carousel.Slide>
      ))}
    </Carousel>
  );
};
